import React, { useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { changePassword } from '../apiCalls';
import * as Sentry from "@sentry/react";
import { calculatePasswordStrength } from '../utils/passwordUtils';

const ChangePasswordModal = ({ visible, onClose, email }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            await changePassword(email, values.currentPassword, values.newPassword, values.confirmPassword);
            enqueueSnackbar(t('changePassword.success'), { variant: 'success' });
            form.resetFields();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('changePassword.error.generic'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setPasswordStrength(0);
        onClose();
    };

    return (
        <Modal
            title={t('changePassword.title')}
            open={visible}
            onCancel={handleClose}
            footer={null}
            centered
        >
            <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                style={{ marginTop: 20 }}
            >
                <Form.Item
                    label={t('changePassword.currentPassword')}
                    required
                    name="currentPassword"
                    rules={[{ required: true, message: t('changePassword.validation.currentRequired') }]}
                >
                    <Input.Password placeholder={t('changePassword.currentPlaceholder')} maxLength={128} />
                </Form.Item>

                <Form.Item
                    label={t('changePassword.newPassword')}
                    required
                    name="newPassword"
                    rules={[
                        { required: true, message: t('changePassword.validation.newRequired') },
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                if (value.length < 8) {
                                    return Promise.reject(new Error(t('auth.validation.passwordMin')));
                                }
                                if (!/[A-Z]/.test(value)) {
                                    return Promise.reject(new Error(t('auth.validation.passwordUppercase')));
                                }
                                if (!/[a-z]/.test(value)) {
                                    return Promise.reject(new Error(t('auth.validation.passwordLowercase')));
                                }
                                if (!/[0-9]/.test(value)) {
                                    return Promise.reject(new Error(t('auth.validation.passwordNumber')));
                                }
                                if (!/[!@#$%^&*]/.test(value)) {
                                    return Promise.reject(new Error(t('auth.validation.passwordSpecial')));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <Input.Password
                        placeholder={t('changePassword.newPlaceholder')}
                        onChange={(e) => setPasswordStrength(calculatePasswordStrength(e.target.value))}
                        maxLength={128}
                    />
                </Form.Item>
                <div style={{ marginTop: -12, marginBottom: 16, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
                    <div
                        style={{
                            height: '100%',
                            width: `${passwordStrength}%`,
                            background: passwordStrength < 60 ? '#ff4d4f' : passwordStrength < 100 ? '#faad14' : '#52c41a',
                            borderRadius: 2,
                            transition: 'all 0.3s'
                        }}
                    />
                </div>

                <Form.Item
                    label={t('changePassword.confirmPassword')}
                    required
                    name="confirmPassword"
                    rules={[
                        { required: true, message: t('auth.validation.confirmRequired') },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('auth.validation.passwordsDoNotMatch')));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder={t('changePassword.confirmPlaceholder')} maxLength={128} />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        size="large"
                    >
                        {t('changePassword.submit')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ChangePasswordModal;
